import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, FileText, Folder, Loader2, Rocket, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface CanvasProjectSummary {
  id: number;
  name: string;
  color?: string;
  isFolder: boolean;
  parentId?: number | null;
}

/**
 * Destination picker shown when the user clicks Build Campaign — pick an
 * existing canvas or create a new one; the chosen projectId is handed back to
 * the build flow. The build NEVER silently reuses the active/most-recent
 * project anymore; destination is always an explicit user choice.
 *
 * Deliberately a plain fixed overlay styled like canvas-clone-picker.tsx (NOT
 * a shadcn Dialog): the explicit card width is what keeps long project names
 * truncating instead of blowing the layout out sideways — the first Dialog
 * version overflowed the modal edge on exactly these names.
 */
export function BuildDestinationPicker({
  brandId,
  setName,
  adCount,
  onPicked,
  onClose,
}: {
  brandId: number;
  setName: string;
  adCount: number;
  onPicked: (projectId: number) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  // v6.76 — real editable VALUE prefilled with the default (same as the
  // clone picker, Dan: "same as what we did earlier today").
  const [newName, setNewName] = useState(() => `Ad images — ${setName}`.slice(0, 80));
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  // Closing is ALWAYS allowed — via Escape, backdrop, or the X — even while a
  // create request is in flight (an in-flight POST is aborted on unmount, so
  // no build can start from a picker the user dismissed). The first version
  // blocked every close path during isCreating, trapping the user until the
  // network round-trip finished.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      abortRef.current?.abort();
    };
  }, [onClose]);

  const defaultName = `Ad images — ${setName}`.slice(0, 80);

  // Same query key as the clone picker so the two share a cache entry.
  const { data: allProjects = [], isLoading } = useQuery<CanvasProjectSummary[]>({
    queryKey: ["/api/canvas/projects", "all"],
    queryFn: async () => {
      const resp = await fetch("/api/canvas/projects", { credentials: "include" });
      if (!resp.ok) return [];
      return resp.json();
    },
    staleTime: 30000,
  });

  const folders = allProjects.filter((p) => p.isFolder);
  const tasksInFolder = (folderId: number) =>
    allProjects.filter((p) => !p.isFolder && p.parentId === folderId);

  const toggleFolder = (folderId: number) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  async function handleCreateNew() {
    setIsCreating(true);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const resp = await fetch("/api/canvas/projects", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        signal: ac.signal,
        body: JSON.stringify({
          name: newName.trim() || defaultName,
          color: "#6366F1",
          parentId: selectedFolderId ?? null,
          isFolder: false,
          brandId,
        }),
      });
      if (!resp.ok) throw new Error("Failed to create canvas");
      const proj = await resp.json();
      qc.invalidateQueries({ queryKey: ["/api/canvas/projects", "all"] });
      qc.invalidateQueries({ queryKey: ["/api/canvas/projects", "rootOnly"] });
      if (selectedFolderId) {
        qc.invalidateQueries({ queryKey: ["/api/canvas/projects", selectedFolderId, "tasks"] });
      }
      onPicked(proj.id);
    } catch (err) {
      // User dismissed the picker mid-create — stay silent, no build starts.
      if (ac.signal.aborted) return;
      const msg = err instanceof Error ? err.message : String(err);
      toast({ description: `Couldn't create canvas: ${msg}`, variant: "destructive" });
      setIsCreating(false);
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        data-testid="build-destination-picker"
        style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid #DCE3ED", boxShadow: "0 24px 60px rgba(0,0,0,0.18)", width: 460, maxWidth: "92vw", maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #EAF0F7" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <Rocket style={{ width: 16, height: 16, color: "#6366F1", flexShrink: 0 }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1A1A1A" }}>Generate ad images</span>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, color: "#5F6D82" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#EAF0F7"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
          >
            <X style={{ width: 15, height: 15 }} />
          </button>
        </div>

        {/* Subtitle */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #EAF0F7" }}>
          <p style={{ fontSize: 13, fontWeight: 500, color: "#1A1A1A", margin: "0 0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{setName}</p>
          <p style={{ fontSize: 11, color: "#5F6D82", margin: 0 }}>
            Generate {adCount} ad images from your reviewed concepts. Choose a canvas for the images. Generation uses credits; ads are not published.
          </p>
        </div>

        {/* Scrollable body — v6.76 reorder to match the Clone Ad picker
            (Dan: "same as what we did earlier today"): New Canvas at the TOP
            with an editable prefilled name; the flat Existing Canvases list
            is gone — brand folders only, expandable to their tasks. */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 0" }}>
          {/* New canvas */}
          <p style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", padding: "4px 20px 8px" }}>New Canvas</p>
          <div style={{ padding: "0 20px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
            <input
              data-testid="build-dest-name"
              type="text"
              placeholder="Canvas name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={isCreating}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #DCE3ED", background: "#FFFFFF", fontSize: 13, color: "#1A1A1A", outline: "none", boxSizing: "border-box" }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#94A3B8"; e.currentTarget.select(); }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "#DCE3ED"; }}
            />
            {folders.length > 0 && (
              <select
                data-testid="build-dest-folder-select"
                value={selectedFolderId ?? ""}
                onChange={(e) => setSelectedFolderId(e.target.value ? Number(e.target.value) : null)}
                disabled={isCreating}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #DCE3ED", background: "#F7F9FC", fontSize: 13, color: "#334155", outline: "none" }}
              >
                <option value="">No brand folder (loose canvas)</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            )}
          </div>

          <div style={{ height: 1, background: "#EAF0F7", margin: "2px 0 6px" }} />

          {/* Brand folders → their tasks */}
          <p style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", padding: "4px 20px 6px" }}>Brands</p>

          {isLoading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
              <Loader2 style={{ width: 18, height: 18, color: "#94A3B8" }} className="animate-spin" />
            </div>
          )}

          {!isLoading && folders.length === 0 && (
            <p style={{ fontSize: 13, color: "#94A3B8", textAlign: "center", padding: "16px 20px" }}>
              No brand folders yet — name the canvas above and create it
            </p>
          )}

          {!isLoading &&
            folders.map((folder) => (
              <div key={folder.id}>
                <button
                  type="button"
                  data-testid={`build-dest-folder-${folder.id}`}
                  onClick={() => toggleFolder(folder.id)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "8px 20px", border: "none", background: "transparent", color: "#1A1A1A", fontSize: 13, cursor: "pointer", textAlign: "left", fontWeight: 500 }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#F1F5F9"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                >
                  <Folder style={{ width: 14, height: 14, color: "#5F6D82", flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{folder.name}</span>
                  <ChevronRight
                    style={{ width: 13, height: 13, color: "#C8D3E2", flexShrink: 0, transition: "transform 0.15s", transform: expandedFolders.has(folder.id) ? "rotate(90deg)" : "rotate(0deg)" }}
                  />
                </button>
                {expandedFolders.has(folder.id) && (
                  <>
                    {tasksInFolder(folder.id).map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        data-testid={`build-dest-task-${task.id}`}
                        onClick={() => onPicked(task.id)}
                        disabled={isCreating}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "7px 20px 7px 44px", border: "none", background: hoveredId === task.id ? "#F1F5F9" : "transparent", color: "#334155", fontSize: 12.5, cursor: "pointer", textAlign: "left" }}
                        onMouseEnter={() => setHoveredId(task.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        <FileText style={{ width: 12, height: 12, color: "#94A3B8", flexShrink: 0 }} />
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.name}</span>
                        <ChevronRight style={{ width: 12, height: 12, color: "#C8D3E2", flexShrink: 0 }} />
                      </button>
                    ))}
                    {tasksInFolder(folder.id).length === 0 && (
                      <p style={{ fontSize: 12, color: "#94A3B8", padding: "4px 20px 8px 44px", margin: 0 }}>No canvases in this brand yet</p>
                    )}
                  </>
                )}
              </div>
            ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid #EAF0F7" }}>
          <button
            data-testid="build-dest-create"
            onClick={handleCreateNew}
            disabled={isCreating}
            style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: "none", background: isCreating ? "#DCE3ED" : "#6366F1", color: isCreating ? "#94A3B8" : "#FFFFFF", fontSize: 13.5, fontWeight: 600, cursor: isCreating ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
          >
            {isCreating ? (
              <>
                <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                Creating…
              </>
            ) : (
              `Create canvas & generate ${adCount} images`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
